import type { SpellDef, SpellId } from '../types';

/**
 * MVP spell table. Costs and cooldowns follow the GDD; damage, force, range and speed are
 * first-pass proposals to tune in playtests. `perLevel` values are also first-pass proposals;
 * `spellStats` resolves a level from them.
 */
export const SPELLS = {
  fireball: {
    id: 'fireball',
    name: 'Fireball',
    slot: 'Q',
    kind: 'projectile',
    cost: 0,
    upgradeCost: 8,
    maxLevel: 4,
    cooldownMs: 1500,
    damage: 10,
    force: 420,
    range: 650,
    speed: 900,
    radius: 14,
    blockedByStones: true,
    guardable: true,
    perLevel: { damage: 2, force: 40, cooldownMs: -100 },
  },
  homing: {
    id: 'homing',
    name: 'Homing',
    slot: 'W',
    kind: 'projectile',
    cost: 14,
    upgradeCost: 9,
    maxLevel: 4,
    cooldownMs: 7000,
    damage: 12,
    force: 380,
    range: 900,
    speed: 520,
    radius: 16,
    detectRadius: 350,
    turnRate: 3.5,
    blockedByStones: true,
    guardable: true,
    perLevel: { damage: 2, force: 30, cooldownMs: -500 },
  },
  lightning: {
    id: 'lightning',
    name: 'Lightning',
    slot: 'E',
    kind: 'instant',
    cost: 12,
    upgradeCost: 8,
    maxLevel: 4,
    cooldownMs: 5000,
    damage: 8,
    force: 360,
    range: 550,
    blockedByStones: true,
    guardable: true,
    perLevel: { damage: 2, force: 30, range: 50 },
  },
  meteor: {
    id: 'meteor',
    name: 'Meteor',
    slot: 'R',
    kind: 'area',
    cost: 20,
    upgradeCost: 12,
    maxLevel: 4,
    cooldownMs: 14000,
    damage: 20,
    force: 650,
    range: 600,
    radius: 120,
    delayMs: 900,
    blockedByStones: false,
    guardable: true,
    perLevel: { damage: 4, force: 60, cooldownMs: -1000 },
  },
  teleport: {
    id: 'teleport',
    name: 'Teleport',
    slot: 'D',
    kind: 'blink',
    cost: 12,
    upgradeCost: 8,
    maxLevel: 4,
    cooldownMs: 12000,
    damage: 0,
    force: 0,
    range: 450,
    blockedByStones: false,
    guardable: false,
    perLevel: { cooldownMs: -1000, range: 50 },
  },
  thrust: {
    id: 'thrust',
    name: 'Thrust',
    slot: 'D',
    kind: 'dash',
    cost: 12,
    upgradeCost: 8,
    maxLevel: 4,
    cooldownMs: 10000,
    damage: 10,
    force: 480,
    range: 400,
    speed: 1400,
    radius: 30,
    blockedByStones: true,
    guardable: true,
    perLevel: { damage: 2, force: 40, cooldownMs: -1000 },
  },
  shield: {
    id: 'shield',
    name: 'Shield',
    slot: 'F',
    kind: 'self',
    cost: 14,
    upgradeCost: 9,
    maxLevel: 4,
    cooldownMs: 12000,
    damage: 0,
    force: 0,
    range: 0,
    durationMs: 1500,
    blockedByStones: false,
    guardable: false,
    perLevel: { cooldownMs: -1000, durationMs: 250 },
  },
  link: {
    id: 'link',
    name: 'Link',
    slot: 'C',
    kind: 'hook',
    cost: 14,
    upgradeCost: 9,
    maxLevel: 4,
    cooldownMs: 10000,
    damage: 4,
    force: 0,
    range: 600,
    speed: 800,
    radius: 16,
    durationMs: 2500,
    pullSpeed: 900,
    blockedByStones: true,
    guardable: true,
    perLevel: { cooldownMs: -1000, durationMs: 250 },
  },
} as const satisfies Record<SpellId, SpellDef>;

/** Spells every player owns at the start of a match. */
export const STARTING_SPELLS: readonly SpellId[] = ['fireball'];

export function getSpell(id: SpellId): SpellDef {
  return SPELLS[id];
}
